import { AbstractControl, FormArray, FormGroup, ValidationErrors } from '@angular/forms';
import { MatSnackBar, MatSnackBarConfig } from '@angular/material/snack-bar';
import { TypedFormControl } from '../shared-models/typed-form-control';
import { map, min } from 'rxjs';
import {
  colors,
  Constants,
  CreditCardValidators,
  GoogleTagManagerConstants,
  ChartYAxisIntervals,
  commonFileTypes,
} from '../constants/constants';
// xlsx is heavy (~800KB+) and only used in the three export/download helpers below.
// It is loaded lazily via dynamic import() inside those functions so it stays OUT of the
// eager vendor bundle (helper.ts is imported by app.component and many always-loaded files).
// This is a type-only import — erased at compile time, so it adds nothing to the bundle.
import type * as XLSXTypes from 'xlsx';
import { CommonService } from '../shared-services/common.service';
import { AICreativeTypes, WorkingEnvironment, snackBarType } from '../constants/enum';
import { Messages } from '../constants/messages';
import { Buffer } from 'buffer';

import { ActivatedRoute, Router } from '@angular/router';

/**
 * Map RactiveForm values to Proto models
 *
 * @param {FormGroup} form - It reperesents Reactive Form.
 * @param {any} model - It reperesents Proto Request or Response Model.
 * @param {number} startIndex - It reperesents the index from where Proto fields start in form group i.e. Form Fields which are not in proto should be at top so that after start index each form control is in sequence with proto model.
 *
 * @remarks
 * This function can only map correct values from ReactiveForm to Proto model
 * when Both values are in sequence i.e if in proto firstName = 1
 * then in FormGroup firstName should be the first FormControl.
 *
 */

export function bindFormToModel(form: FormGroup, model: any, startIndex: number = 0) {
  const formValues = form.value;
  Object.keys(formValues).forEach((key, index) => {
    if (index >= startIndex) model.array[index] = formValues[key];
  });
}

export function getTemplateTypeName(templateType: number): string {
  switch (templateType) {
    case AICreativeTypes.SingleAsset:
      return 'Single Image';
    case AICreativeTypes.MultiAsset:
      return 'Multi Image';
    case AICreativeTypes.TextOnly:
      return 'Text Only';
    default:
      return '';
  }
}

export function bindTypedFormToModel(form: FormGroup, model: any, startIndex: number = 0) {
  const formValues = form.value;
  Object.keys(formValues).forEach((key, index) => {
    if (index >= startIndex) {
      const formControl = form.get(key);

      if (formControl instanceof TypedFormControl) {
        model.array[index] = formControl.getValue();
      } else if (formControl instanceof FormArray) {
        model.array[index] = [];
        const formArray = formControl as FormArray;
        formArray.controls.forEach((control: AbstractControl, arrayIndex: number) => {
          if (control instanceof TypedFormControl) {
            model.array[index].push(control.getValue());
          }
        });
      } else {
        model.array[index] = formValues[key];
      }
    }
  });
}

export function showErrorSnackBar(snackBar: MatSnackBar, msg: string) {
  if (msg) {
    let config = new MatSnackBarConfig();
    config.duration = 2000;
    config.panelClass = ['red-snackbar'];
    snackBar.open(msg, '', config);
  }
}

export function showSuccessSnackBar(snackBar: MatSnackBar, msg: string) {
  if (msg) {
    let config = new MatSnackBarConfig();
    config.duration = 3000;
    config.panelClass = ['green-snackbar'];
    snackBar.open(msg, '', config);
  }
}

export const assignIsSelected = (list: any) => {
  return list.map((item: any) => {
    return { ...item, ...{ isSelected: false } };
  });
};

export const checkAllSelected = (array: any) => {
  return array.every((item: any) => item.isSelected);
};

/**
 * @param array Array on which filter should apply
 * @param filterBy key on which filter should apply (default is 'id').
 */
export const getSelectedItemIds = (array: any, filterBy: string = 'id') => {
  return array.filter((item: any) => item.isSelected).map((item: any) => item[filterBy]);
};

/**
 * @param array Array on which filter should apply
 * @param filterBy key on which filter should apply (default is 'id').
 */
export const getItemIds = (array: any) => {
  return array.map((item: any) => item.id);
};

/**
 * @param array Array on which filter should apply
 */
export const getSelectedItems = (array: any) => {
  return array.filter((item: any) => item.isSelected);
};

export const isAnyItemSelected = (array: any) => {
  return array.find((item: any) => item.isSelected) ? true : false;
};

export const getSortedColumns = (sourceArr: string[], targetArr: any[]) => {
  sourceArr.forEach((sourceItem: any, index: number) => {
    targetArr.forEach((targetItem: any) => {
      if (sourceItem === targetItem.columnname.value) {
        targetItem.columnorder = index + 1;
      }
    });
  });
};

export const getIndexById = (array: any, id: any) => {
  return array.findIndex((item: any) => item.id === id);
};

export const getStarRatings = (rating: string) => {
  let intRating: number = 0;
  rating = rating.toLowerCase();
  if (rating === 'onestar' || rating === 'one') {
    intRating = 1;
  } else if (rating === 'twostar' || rating === 'two') {
    intRating = 2;
  } else if (rating === 'threestar' || rating === 'three') {
    intRating = 3;
  } else if (rating === 'fourstar' || rating === 'four') {
    intRating = 4;
  } else if (rating === 'fivestar' || rating === 'five') {
    intRating = 5; // or adjust based on your requirements
  } else {
    intRating = 0;
  }

  return intRating;
};

function getExtensionFromContentType(contentType: string | null): string {
  switch ((contentType ?? '').toLowerCase()) {
    case 'image/png':
      return '.png';
    case 'image/jpeg':
    case 'image/jpg':
      return '.jpg';
    case 'image/webp':
      return '.webp';
    case 'video/mp4':
      return '.mp4';
    case 'video/quicktime':
      return '.mov';
    case 'video/x-matroska':
      return '.mkv';
    default:
      return '';
  }
}

function hasFileExtension(fileName: string): boolean {
  return /\.[a-z0-9]+$/i.test(fileName);
}

export function normalizeDownloadFileName(fileName: string, contentType: string | null): string {
  if (hasFileExtension(fileName)) {
    return fileName;
  }

  const extension = getExtensionFromContentType(contentType);
  return extension ? `${fileName}${extension}` : fileName;
}

/**
 * A creative's name reduced to a download filename a filesystem will accept:
 * "Dewy Setting Mist Try-On" -> "dewy-setting-mist-try-on.mp4". Falls back to
 * `fallbackStem` when the name is empty or has nothing filename-safe left in it.
 */
export function creativeDownloadFileName(
  title: string | null | undefined,
  mediaKind: string,
  fallbackStem: string = 'creative'
): string {
  const extension = mediaKind === 'video' ? 'mp4' : 'png';
  const stem = (title ?? '')
    .replace(/[^a-z0-9]+/gi, '-')
    .replace(/^-+|-+$/g, '')
    .toLowerCase();

  return `${stem || fallbackStem}.${extension}`;
}

export function triggerBlobDownload(blob: Blob, fileName: string): void {
  const downloadUrl = window.URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = downloadUrl;
  link.download = normalizeDownloadFileName(fileName, blob.type);
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  window.URL.revokeObjectURL(downloadUrl);
}

export async function downloadRemoteAsset(url: string, fileName: string): Promise<void> {
  const response = await fetch(url);
  const blob = await response.blob();
  // iOS-aware: Web Share on iOS (no page navigation), silent download elsewhere.
  await downloadOrShareBlob(blob, fileName);
}

// True on iOS / iPadOS. There, <a download> is ignored — a programmatic click
// navigates to the file (opens the image) and hijacks the page, so any redirect
// the caller runs afterwards never happens. We share instead of download there.
export function isIOS(): boolean {
  return /iP(ad|hone|od)/.test(navigator.userAgent)
    || (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1);
}

// Cross-platform "download": Android/desktop keep the normal silent download.
// iOS uses the Web Share sheet, which does NOT navigate the page — so a redirect
// the caller runs afterwards survives. Share errors (user cancel / no user
// gesture) are swallowed so the caller's flow (e.g. router.navigate) continues.
export async function downloadOrShareBlob(blob: Blob, fileName: string): Promise<void> {
  if (isIOS()) {
    try {
      const file = new File([blob], normalizeDownloadFileName(fileName, blob.type), { type: blob.type });
      if (typeof navigator.canShare === 'function' && navigator.canShare({ files: [file] })) {
        await navigator.share({ files: [file] });
      }
    } catch {
      // user cancelled, or no transient activation for share — ignore so the
      // caller's redirect still runs (unlike <a download>, this never navigates).
    }
    return;
  }
  triggerBlobDownload(blob, fileName);
}

// export const selectFile = async (
//   event: any,
//   isBusinessLogo: boolean = false,
//   fileIndex: number = 0,
//   returnFullData: boolean = false // Flag to decide the structure of the returned data
// ): Promise<any> => {
//   const file = event.target.files[fileIndex];

//   if (file) {
//     let maxSizeInBytes = 0;

//     if (file.type.startsWith('image/')) {
//       // For images, set a maximum file size of 8MB
//       maxSizeInBytes = Constants.MaxImageFileSize * 1024 * 1024; // 8MB in bytes

//       // Format check for image (allow only JPG, PNG, HEIC)
//       const validImageFormats = ['image/png', 'image/jpeg', 'image/heic'];
//       if (!validImageFormats.includes(file.type)) {
//         throw Messages.ImageFormatMessage;
//       }

//     } else if (file.type.startsWith('video/')) {
//       // For videos, set a maximum file size of 20MB

//       // Format check for video (allow only MP4, MKV, MOV)
//       const validVideoFormats = ['video/mp4', 'video/x-matroska', 'video/quicktime'];
//       if (!validVideoFormats.includes(file.type)) {
//         throw Messages.VideoFormatMessage;

//       }

//     }

//     else if (file.type.startsWith('audio/')) {
//       throw Messages.AudioFormatMessage;

//     }
//     else {
//       if (isBusinessLogo) throw 'Please select an image';
//     }

//     if (file.size <= maxSizeInBytes) {
//       const data = await readAsync(file, file.name);
//       if (returnFullData) {
//         // Return full data including fileType and fileSize
//         return {
//           data,               // Original file data
//           fileType: file.type, // File type (MIME type)
//           fileSize: file.size  // File size in bytes
//         };
//       } else {
//         // Return only the file data
//         return data;
//       }
//     } else {
//       // File size exceeds the allowed limit
//       if (file.type.startsWith('image/')) {
//         throw 'File size exceeds the allowed ' + Constants.MaxImageFileSize + 'MB limit.';
//       } else {
//         // throw 'File size exceeds the allowed ' + Constants.MaxVideoAndDocumentFileSize + 'MB limit.';
//       }
//     }
//   }

//   console.log('data');
//   return null; // Return null if no file was selected
// };

export const selectFile = async (
  event: any,
  isBusinessLogo: boolean = false,
  fileIndex: number = 0,
  returnFullData: boolean = false, // Flag to decide the structure of the returned data
): Promise<any> => {
  const file = event.target.files[fileIndex];
  if (!file) {
    return null; // Return null if no file was selected
  }

  // Initialize max size variable
  let maxSizeInBytes = 0;

  // If returnFullData is true, perform file type validations
  if (returnFullData) {
    // Define valid formats
    const validFormats = {
      image: ['image/png', 'image/jpeg', 'image/heic'],
      video: ['video/mp4', 'video/x-matroska', 'video/quicktime'],
    };

    // Check the file type
    if (file.type.startsWith('image/')) {
      maxSizeInBytes = Constants.MaxImageFileSize * 1024 * 1024; // 8MB for images

      if (!validFormats.image.includes(file.type)) {
        throw Messages.ImageFormatMessage;
      }
    } else if (file.type.startsWith('video/')) {
      maxSizeInBytes = 1024 * 1024 * 1024; // 1GB for videos

      if (!validFormats.video.includes(file.type)) {
        throw Messages.VideoFormatMessage;
      }
    } else if (
      file.type === 'application/pdf' ||
      file.type === 'application/msword' ||
      file.type === 'application/vnd.openxmlformats-officedocument.wordprocessingml.document'
    ) {
      throw 'PDF and DOC files are not supported.'; // Reject PDF and DOC files
    } else if (file.type.startsWith('audio/')) {
      throw Messages.AudioFormatMessage; // Reject audio files
    } else {
      // For unsupported formats
      throw 'File format not supported.'; // Throw error for unsupported formats
    }
  }

  // Check for file size only if returnFullData is false
  if (!returnFullData) {
    if (file.type.startsWith('image/') && file.size > 8 * 1024 * 1024) {
      throw `File size exceeds the allowed limit of ${Constants.MaxImageFileSize}MB for images.`;
    }

    if (file.type.startsWith('video/') && file.size > 20 * 1024 * 1024) {
      throw `File size exceeds the allowed limit of 20MB for videos.`;
    }
  }

  // Read file data
  const data = await readAsync(file, file.name);
  return returnFullData
    ? {
        data, // Original file data
        fileType: file.type, // File type (MIME type)
        fileSize: file.size, // File size in bytes
      }
    : data; // Return raw data if returnFullData is false
};

export const readAsync = (file: any, fName: any): Promise<any> => {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    let data = {};
    reader.readAsDataURL(file);
    reader.onload = (e) => {
      data = {
        fileData: e?.target?.result?.toString().split(',')[1],
        fileName: fName,
      };

      resolve(data);
    };
    reader.onerror = () => {
      reject(new Error('Unable to read..'));
    };
  });
};

export const extractFileDimensions = async (
  files: FileList,
): Promise<
  { file: File; fileType: string; videoDuration: number; width: number; height: number; aspectRatio: string }[]
> => {
  let response: any[] = [];
  return new Promise((resolve, reject) => {
    let processedFiles = 0;

    const checkResolve = () => {
      if (processedFiles === files.length) {
        resolve(response);
      }
    };

    for (let i = 0; i < files.length; i++) {
      if (files[i].type.startsWith(commonFileTypes.image + '/')) {
        const img = new Image();
        img.onload = () => {
          const fileType = commonFileTypes.image;
          const videoDuration = 0;
          const width = img.naturalWidth;
          const height = img.naturalHeight;
          const aspectRatio = `${width}:${height}`;
          response.push({ file: files[i], fileType, videoDuration, width, height, aspectRatio });
          processedFiles++;
          checkResolve();
        };
        img.onerror = (err) => reject(new Error('Failed to load image.'));
        img.src = URL.createObjectURL(files[i]);
      } else if (files[i].type.startsWith(commonFileTypes.video + '/')) {
        const video = document.createElement(commonFileTypes.video) as HTMLVideoElement;
        video.onloadedmetadata = () => {
          const fileType = commonFileTypes.video;
          const videoDuration = video.duration;
          const width = video.videoWidth;
          const height = video.videoHeight;
          const aspectRatio = `${width}:${height}`;
          response.push({ file: files[i], fileType, videoDuration, width, height, aspectRatio });
          processedFiles++;
          checkResolve();
        };
        video.onerror = (err) => reject(new Error('Failed to load video.'));
        video.src = URL.createObjectURL(files[i]);
        video.load();
      } else {
        reject(new Error('Unsupported file type for dimension extraction.'));
      }
    }
  });
};

export function getColors(initials: string): string {
  if (!initials) {
    return '';
  }
  const index = getFirstLetterIndex(initials);
  return colors[index % colors.length];
}

export function getFirstLetterIndex(initials: string): number {
  const firstLetter = initials[0]?.toUpperCase();
  const charCode = firstLetter?.charCodeAt(0);
  return charCode - 'A'.charCodeAt(0);
}

export function updateDataSourceForContact(contacts: any) {
  if (contacts == undefined) return [];
  return contacts.map((item: any) => {
    return {
      ...item,
      displayName: item.contactname
        ? item.contactname.value
        : item.fullname
          ? item.fullname.value
          : item.name
            ? item.name.value
            : item.firstname
              ? item.firstname?.value
              : '',
      id: item.id ? item.id : item.contactid ? item.contactid : 0,
      isSelected: false,
    };
  });
}

export function updateDataSourceForState(getAllStatesList: any) {
  if (getAllStatesList == undefined) return [];
  return getAllStatesList.map((item: any) => {
    return {
      ...item,
      displayName: item.statename.value,
      id: item.statecode.value,
    };
  });
}

export function updateDataSourceForCountries(countriesList: any) {
  if (countriesList == undefined) return [];
  return countriesList.map((item: any) => {
    return {
      ...item,
      displayName: item?.countryname?.value,
      id: item?.countrycode?.value,
    };
  });
}

export function updateDataSourceForYear(getAllYearList: any) {
  if (getAllYearList == undefined) return [];
  return getAllYearList.map((item: any, index: number) => {
    return {
      ...item,
      displayName: item,
      id: index + 1,
    };
  });
}

export function updateDataSourceForOnboarding(dataList: any) {
  if (dataList == undefined) return [];
  return dataList.map((item: any) => {
    return {
      ...item,
      displayName: item.name?.value,
      id: item.id,
    };
  });
}
/*
export const getNameShortCode = (fullName: string) => {
  let nameShortCode = '';
  if (fullName) {
    fullName = fullName.trim();
    if (fullName.includes(' ')) {
      let arr = fullName.split(' ');
      let nameFirstCharacter = '';
      let nameSecondCharacter = '';
      if (arr.length > 2) {
        for (let i = 0; i < arr.length; i++) {
          if (!arr[i].trim()) {
            arr.splice(i, 1);
            i--;
          }
        }
      }
      if (arr.length > 0) {
        nameFirstCharacter = arr[0].trim().substring(0, 1);
      }
      if (arr.length > 1) {
        nameSecondCharacter = arr[1].trim().substring(0, 1);
      }
      if (nameFirstCharacter && nameSecondCharacter) {
        nameShortCode = nameFirstCharacter + nameSecondCharacter;
      } else if (nameFirstCharacter) {
        nameSecondCharacter = arr[0].trim().substring(1, 2);
        nameShortCode = nameFirstCharacter + nameSecondCharacter;
      } else if (nameSecondCharacter) {
        nameFirstCharacter = arr[1].trim().substring(1, 2);
        nameShortCode = nameFirstCharacter + nameSecondCharacter;
      }
    } else {
      if (fullName.length > 2) {
        nameShortCode = fullName.substring(0, 2);
      } else {
        nameShortCode = fullName.substring(0, 1);
      }
    }
  } else {
    nameShortCode = 'N/A';
  }
  if (nameShortCode) {
    nameShortCode = nameShortCode.toUpperCase();
  }
  return nameShortCode;
};
*/
// Lazily resolve the xlsx module (ESM build). Kept in one place so all three helpers
// share the same dynamic import, and webpack splits xlsx into its own on-demand chunk.
async function loadXLSX(): Promise<typeof XLSXTypes> {
  const mod: any = await import('xlsx');
  // Handle both ESM namespace and CJS-interop (`.default`) shapes.
  return mod.utils ? mod : mod.default;
}

export class TableUtil {
  static async exportTableToExcel(tableId: string, name?: string) {
    const XLSX = await loadXLSX();
    let { sheetName, fileName } = getFileName(name);
    let targetTableElm = document.getElementById(tableId);
    let wb = XLSX.utils.table_to_book(targetTableElm, <XLSXTypes.Table2SheetOpts>{
      sheet: sheetName,
    });
    XLSX.writeFile(wb, `${fileName}.xlsx`);
  }
  static async exportArrayToExcel(arr: any[], name?: string) {
    const XLSX = await loadXLSX();
    let { sheetName, fileName } = getFileName(name);
    var wb = XLSX.utils.book_new();
    var ws = XLSX.utils.json_to_sheet(arr);
    XLSX.utils.book_append_sheet(wb, ws, 'ExportResult');
    XLSX.writeFile(wb, `${name}.xlsx`);
  }
}

const getFileName = (name: string | undefined) => {
  let timeSpan = new Date().toISOString();
  let sheetName = name || 'ExportResult';
  let fileName = `${sheetName}-${timeSpan}`;
  return {
    sheetName,
    fileName,
  };
};

export function converToByteArr(bytes: any) {
  var base64Index = bytes.indexOf(';base64,') + ';base64,'.length;
  var base64 = bytes.substring(base64Index);
  var raw = window.atob(base64);
  var rawLength = raw.length;
  var array = new Uint8Array(rawLength);
  for (var i = 0; i < rawLength; i++) {
    array[i] = raw.charCodeAt(i);
  }
  return array;
}

export function getRating(rating: string): number {
  if (rating == null || rating == undefined) {
    return 0;
  }
  rating = rating.toLowerCase();
  if (rating === 'one') {
    return 1;
  } else if (rating === 'two') {
    return 2;
  } else if (rating === 'three') {
    return 3;
  } else if (rating === 'four') {
    return 4;
  } else if (rating === 'five') {
    return 5;
  } else {
    return 0;
  }
}

export function getRatingStars(rating?: any): string {
  // Get the value
  const val = parseFloat(rating);
  // Turn value into number/100
  const size = (val / 5) * 100;
  return size + '%';
}

export function getErrorMessage(statusMessage: string): string {
  let message = statusMessage.split('Detail=')[1];
  let n = message.lastIndexOf(')');
  message = message.substring(0, n);
  return message;
}

export function roundToTwoDecimal(n: number): number {
  return Math.round((n + Number.EPSILON) * 100) / 100;
}

export class actions {
  constructor(private commonService: CommonService) {}

  disableAction = () => {
    if (this.commonService?.businessSubscription?.istrialallowed) {
      let nowDate = new Date();
      let subscriptionEndDate = new Date(this.commonService.businessSubscription?.subscriptionendson);

      if (subscriptionEndDate < nowDate) {
        if (!this.commonService.actionRestrictions.isDisableAction) {
          this.commonService.actionRestrictions.isDisableAction = true;
        }
        this.commonService.actionRestrictions.reasons.push('TrialExpired');
      }
    }

    if (this.commonService.businessSubscription?.isextendedtrialallowed) {
      let nowDate = new Date();
      let subscriptionEndDate = new Date(this.commonService.businessSubscription?.subscriptionendson);

      if (subscriptionEndDate < nowDate) {
        if (!this.commonService.actionRestrictions.isDisableAction) {
          this.commonService.actionRestrictions.isDisableAction = true;
        }
        this.commonService.actionRestrictions.reasons.push('ExtendedTrialExpired');
      } else {
        this.commonService.actionRestrictions.isDisableAction = false;
      }
    }

    if (this.commonService.businessSubscription?.storagelimit < this.commonService.businessSubscription.usedmediasize) {
      if (!this.commonService.actionRestrictions.isDisableAction) {
        this.commonService.actionRestrictions.isDisableAction = true;
      }
      this.commonService.actionRestrictions.reasons.push('StorageLimitOver');
    }

    if (
      this.commonService.businessSubscription?.openailimit <
      this.commonService.businessSubscription.usedopenaitokenscount
    ) {
      if (!this.commonService.actionRestrictions.isDisableAction) {
        this.commonService.actionRestrictions.isDisableAction = true;
      }
      this.commonService.actionRestrictions.reasons.push('OpenAITokenLimitOver');
    }

    if (this.commonService.actionRestrictions.isDisableAction) {
      //alert(this.commonService.actionRestrictions.reasons);
    }
  };
}

export function getCurrentEnvironment(): WorkingEnvironment {
  const currentUrl = window.location.origin;

  if (currentUrl.includes('localhost')) return WorkingEnvironment.Local;
  else if (currentUrl.includes('xpappdev.azurewebsites.net')) return WorkingEnvironment.Development;
  else if (currentUrl.includes('xpappqa.azurewebsites.net')) return WorkingEnvironment.QA;
  else if (currentUrl.includes('xpappstaging.azurewebsites.net')) return WorkingEnvironment.Staging;
  else if (currentUrl.includes('app.xoopah.com')) return WorkingEnvironment.Production;
  else return WorkingEnvironment.Local;
}

export function IntervalToDisplayFormat(duration?: string): any {
  if (!duration) {
    return null;
  }

  let arr = duration.split(':');
  let hours = parseInt(arr[0]);
  let minutes = parseInt(arr[1]);
  if (hours === 0 && minutes > 0) {
    return `${minutes}m`;
  }
  if (minutes === 0 && hours > 0) {
    return `${hours}h`;
  }
  return `${hours}h ${minutes}m`;
}

export function minutesToDisplayFormat(minutesString: string): string {
  const durationInMinutes = parseInt(minutesString, 10);
  const hours = Math.floor(durationInMinutes / 60);
  const minutes = durationInMinutes % 60;

  if (hours > 0 && minutes > 0) return `${hours}h ${minutes}m`;
  else if (hours > 0 && minutes == 0) return `${hours}h`;
  return `${minutes}m`;
}

export function durationToMinutes(duration: string): any {
  let parts = duration.split(' ');
  let totalMinutes = 0;

  for (const part of parts) {
    if (part.endsWith('h')) {
      const hours = parseInt(part.slice(0, -1));
      totalMinutes += hours * 60;
    } else if (part.endsWith('m')) {
      const minutes = parseInt(part.slice(0, -1));
      totalMinutes += minutes;
    }
  }
  return totalMinutes.toString();
}

export function UniversalDateFormat(date: string): string {
  let parts = date.split('/');
  let day = parts[0];
  let month = parts[1];
  let year = parts[2];

  return `${year}-${month}-${day}`;
}

export function CustomAudienceDateFormat(date: string): string {
  let parts = date.split(' ')[0].split('/');
  let day = parts[0];
  let month = parts[1];
  let year = parts[2];

  return `${month}/${day}/${year}`;
}

export function getTodayDate() {
  let date = new Date();
  let day = String(date.getDate()).padStart(2, '0');
  let month = String(date.getMonth() + 1).padStart(2, '0');
  let year = date.getFullYear();
  return `${month}/${day}/${year}`;
}

export async function downloadFileFromStream(fileStream: any, fileName: string) {
  const base64String = fileStream;
  if (base64String && typeof base64String === 'string') {
    const XLSX = await loadXLSX();
    const decodedString = atob(base64String);
    const workbook = XLSX.read(decodedString, { type: 'string' });
    XLSX.writeFile(workbook, `${fileName}`);
  }
}

export function getSnackBarType(type: string): any {
  if (type === 'info') {
    return snackBarType.Info;
  }
  if (type === 'success') {
    return snackBarType.Success;
  }
  if (type === 'error') {
    return snackBarType.Error;
  }
  if (type === 'warning') {
    return snackBarType.Warning;
  }
}

export function updateDataSourceForBookingService(getAllServicesList: any) {
  if (getAllServicesList == undefined) return [];
  return getAllServicesList.map((item: any) => {
    return {
      ...item,
      displayName: item.servicename.value,
      id: item.id,
    };
  });
}

export function updateDataSourceForBookingTimeSlot(getAllTimeSlot: any) {
  if (getAllTimeSlot == undefined) return [];
  return getAllTimeSlot.map((item: any, index: number) => {
    return {
      ...item,
      displayName: item.starttime?.value,
      id: index + 1, // Generates a random number between 1 and 1000
    };
  });
}

export function generateMonthsArray(): { name: string; displayName: string; id: number; isSelected: boolean }[] {
  const monthsArray = [];
  for (let i = 1; i <= 12; i++) {
    const monthName = i < 10 ? '0' + i : '' + i;
    monthsArray.push({ name: monthName, displayName: monthName, id: i, isSelected: false });
  }
  return monthsArray;
}

export function getCurrentDate(): string {
  const currentDate: Date = new Date();

  // Format date
  const day: string = String(currentDate.getDate()).padStart(2, '0');
  const month: string = String(currentDate.getMonth() + 1).padStart(2, '0'); // Month is zero-based
  const year: number = currentDate.getFullYear();

  // Format time
  const hours: number = currentDate.getHours();
  const minutes: string = String(currentDate.getMinutes()).padStart(2, '0');
  const seconds: string = String(currentDate.getSeconds()).padStart(2, '0');
  const ampm: string = hours >= 12 ? 'pm' : 'am';
  const formattedHours: number = hours % 12 || 12;

  return `${day}/${month}/${year} ${formattedHours}:${minutes}:${seconds} ${ampm}`;
}

/**
 * Check if two Date objects are same
 *
 * @param {Date} date1 - first date to compare.
 * @param {Date} date2 - second date to compare.
 */
export function isSameDay(date1: Date, date2: Date): boolean {
  return (
    date1.getFullYear() === date2.getFullYear() &&
    date1.getMonth() === date2.getMonth() &&
    date1.getDate() === date2.getDate()
  );
}

/**
 * Prevent entering of non numeric characters.
 *
 * @param {KeyboardEvent} event - the event from triggered from entering
 *
 * @remarks - Currently this is method is being used to prevent non numeric on <input> elements
 */
export function preventNonNumericInput(event: KeyboardEvent) {
  if (event.key !== 'Backspace' && !event.ctrlKey && !event.metaKey && !event.key.match(/^[0-9]$/)) {
    event.preventDefault();
  }
}

export function limitToTwoDecimalPlaces(event: any, form: any, field: any) {
  if (event.code.startsWith('Digit')) {
    let value = event?.target.value + event.key;

    let decimalIndex = value.toString().indexOf('.');

    if (decimalIndex !== -1 && event?.target.value.toString().length - (decimalIndex + 1) == 2) {
      event.preventDefault();
      return;
    }

    // If the decimal point exists and the number of digits after it exceeds two
    if (decimalIndex !== -1 && value.toString().length - (decimalIndex + 1) > 2) {
      // Truncate the value
      value = value.toString().slice(0, decimalIndex + 2);
      form.get(field)?.setValue(value);
    }
  }
}

export function preventPlusMinus(event: any) {
  if (event.key === '+' || event.key === '-') {
    event.preventDefault(); // Prevent default action (typing + or -)
  }
}

export function preventNonNumericCharacters(event: any) {
  if (event.key == 'Backspace' || event.key == 'Tab') {
    //allow backspace
    return;
  }

  var digitPattern = /[0-9]/;

  if (!digitPattern.test(event.key)) {
    event.preventDefault(); // Prevent default action (typing non-numeric characters)
  }
}

export function extractDigitsFromString(inputString: string) {
  let extractedDigits = inputString.match(Constants.matchAllDigitsInStringRegex);
  if (extractedDigits) return extractedDigits.join();
  else return null;
}

/**
 * Check if the input string is not empty.
 *
 * @param {AbstractControl} control - The form control to validate.
 * @returns {ValidationErrors | null} - Returns an object with an error if the value is an empty string, otherwise returns null.
 *
 * @remarks - Assumes that `control.value` is a string|null|undefined
 */
export function notEmptyStringValidator(control: AbstractControl): ValidationErrors | null {
  const value = control.value;
  if (value && value.trim() === '') {
    return { isEmptyString: true };
  } else {
    return null;
  }
}

export function removeFormControlError(control: FormGroup, controlName: string, errorKey: string): void {
  const ctrl = control.get(controlName);

  if (ctrl && ctrl.errors) {
    // Clone the errors object if it exists
    const updatedErrors = { ...ctrl.errors };
    // Delete the specific error key
    delete updatedErrors[errorKey];

    // If no errors remain, set to null, otherwise update with the remaining errors
    ctrl.setErrors(Object.keys(updatedErrors).length === 0 ? null : updatedErrors);
  }
}

//use this method to validate the credit card number length
export function validateCardNumberLength(cardNumber: string, form: any, field: any) {
  if (
    cardNumber.length >= CreditCardValidators.Lengths.minLength &&
    cardNumber.length < CreditCardValidators.Lengths.maxLength + 1
  ) {
    form.get(field)?.setErrors(null);
    return true;
  } else {
    form.get(field)?.setErrors({ invalid: true });
    return false;
  }
}

export type DataLayerKeyValuePairs = [variableName: string, variableValue: string | number | boolean | any[] | null];

/**
 * Sends events to Google Tag Manager with optional data layer elements.
 *
 * This function iterates over the provided events and constructs a data layer object
 * for each event. It then pushes the constructed data layer object to the global
 * `window.dataLayer` array, which is used by Google Tag Manager to track events and
 * associated data.
 *
 * @param {string[]} events - An array of event names to be sent to Google Tag Manager.
 * @param {DataLayerKeyValuePairs[] | null} [dataLayerElements=null] - An optional array of key-value pairs
 * representing additional data to be included in the data layer for each event.
 *
 * @example
 * sendGoogleTagManagerEvent(
 *   ['event1', 'event2'],
 *   [
 *     ['key1', 'value1'],
 *     ['key2', 123],
 *     ['key3', true],
 *     ['key4', ['array', 'of', 'values']],
 *   ]
 * );
 *
 * @remarks
 * - The `dataLayerElements` parameter is optional. If not provided, only the event names will be sent.
 * - The function assumes that `GoogleTagManagerConstants.DataLayerVars.Event` is a valid key in the `GoogleTagManagerConstants.DataLayerVars` object.
 * - Ensure that `window.dataLayer` is defined and accessible in the global scope.
 */
export function sendGoogleTagManagerEvent(events: string[], dataLayerElements: DataLayerKeyValuePairs[] | null = null, isSetBusinessId: boolean = false, businessId: number = 0): void {

  if(isSetBusinessId && businessId > 0) {

  sendGoogleTagManagerEvent(
      [GoogleTagManagerConstants.CustomTriggerEvents.GuestFlowStarted],
      [
        [GoogleTagManagerConstants.DataLayerVars.BusinessId, businessId],
   
      ]
    );
  }

  events.forEach((event) => {
    let appendedLayer: Record<string, string | number | boolean | any[] | null> = {};

    appendedLayer[GoogleTagManagerConstants.DataLayerVars.Event] = event;

    dataLayerElements?.forEach((item) => {
      appendedLayer[item[0]] = item[1];
    });

    window.dataLayer?.push(appendedLayer);
  });
}

export function sendSignupSuccessGoogleTagManagerEvents(
  businessId: number,
  businessName?: string | null,
  businessEmail?: string | null,
) {  
  const dataLayerElements: DataLayerKeyValuePairs[] = [
    [GoogleTagManagerConstants.DataLayerVars.BusinessId, businessId],
    [GoogleTagManagerConstants.DataLayerVars.BusinessName, businessName ?? ''],
    [GoogleTagManagerConstants.DataLayerVars.BusinessEmail, businessEmail ?? ''],
  ];

  sendGoogleTagManagerEvent(
    [
      GoogleTagManagerConstants.CustomTriggerEvents.MXPIdentifyUser,
      GoogleTagManagerConstants.CustomTriggerEvents.MXPRegisterUserProfile,
      GoogleTagManagerConstants.CustomTriggerEvents.MXPRegisterSuperProperties,
    ],
    dataLayerElements,
  );

  sendGoogleTagManagerEvent([
    GoogleTagManagerConstants.CustomTriggerEvents.BusinessUserSignUpSuccessful,
    GoogleTagManagerConstants.CustomTriggerEvents.BusinessUserLoginSuccessful,
  ]);
}

export function addCalendarMonths(date: Date, months: number): Date {
  let newDate = createDateWithOverflow(date.getFullYear(), date.getMonth() + months, date.getDate());

  // It's possible to wind up in the wrong month if the original month has more days than the new
  // month. In this case we want to go to the last day of the desired month.
  // Note: the additional + 12 % 12 ensures we end up with a positive number, since JS % doesn't
  // guarantee this.
  if (newDate.getMonth() != (((date.getMonth() + months) % 12) + 12) % 12) {
    newDate = createDateWithOverflow(newDate.getFullYear(), newDate.getMonth(), 0);
  }

  return newDate;
}

/** Creates a date but allows the month and date to overflow. */
function createDateWithOverflow(year: number, month: number, date: number) {
  // Passing the year to the constructor causes year numbers <100 to be converted to 19xx.
  // To work around this we use `setFullYear` and `setHours` instead.
  const d = new Date();
  d.setFullYear(year, month, date);
  d.setHours(0, 0, 0, 0);
  return d;
}

export function updateDataSourceForSavedAudience(getAllSavedAudienceList: any) {
  if (getAllSavedAudienceList == undefined) return [];
  return getAllSavedAudienceList.map((item: any) => {
    return {
      ...item,
      displayName: item.name,
      itemId: item.id,
    };
  });
}

export function deepCopy<T>(obj: T): T {
  if (obj === null || typeof obj !== 'object') {
    return obj;
  }

  if (Array.isArray(obj)) {
    return obj.map((item) => deepCopy(item)) as unknown as T;
  }

  const copiedObj = {} as T;
  for (const key in obj) {
    if (obj.hasOwnProperty(key)) {
      (copiedObj as any)[key] = deepCopy((obj as any)[key]);
    }
  }

  return copiedObj;
}

// Optional method to scroll down after the file is uploaded

export function scrollDownAfterUpload() {
  const element = document.querySelector('.lastDiv');
  setTimeout(() => {
    if (element) {
      element.scrollIntoView({ block: 'end', behavior: 'smooth' });
    }
  }, 0);
}

export function scrollDownContactDiv() {
  const element = document.querySelector('.contactDiv');
  setTimeout(() => {
    if (element) {
      element.scrollIntoView({ block: 'end', behavior: 'smooth' });
    }
  }, 0);
}

/**
 * Scroll `target` into view inside `container`, and inside nothing else.
 *
 * Element.scrollIntoView() scrolls EVERY scrollable ancestor, the document included.
 * On a phone the document scrolls too, so a listing that reveals more rows and then
 * brings its control into view was dragging the topbar off screen with it. This moves
 * the one scroller that should move.
 *
 * `block` mirrors scrollIntoView's: 'start' puts the target at the top of the
 * container, 'end' at the bottom.
 */
export function scrollIntoContainer(
  container: HTMLElement | null | undefined,
  target: HTMLElement | null | undefined,
  options: { block?: 'start' | 'end'; offset?: number } = {},
): void {
  if (!container || !target) return;

  const { block = 'start', offset = 0 } = options;
  const containerBox = container.getBoundingClientRect();
  const targetBox = target.getBoundingClientRect();

  // Measured rather than read off offsetTop: the target's offset parent is not
  // guaranteed to be the scroller, and this reads the same however the two are nested.
  const delta = block === 'end'
    ? targetBox.bottom - containerBox.bottom
    : targetBox.top - containerBox.top;

  container.scrollTo({
    top: Math.max(0, container.scrollTop + delta + offset),
    behavior: 'smooth',
  });
}

export function updateTrimmedControlValue(
  formGroup: FormGroup,
  controlName: string,
  setRequestCallback: (value: string) => void,
) {
  const control = formGroup.get(controlName);
  if (control) {
    // Set the control value without trimming
    control.setValue(control.value, { emitEvent: false });
    // Trim the value only for the request callback
    const trimmedValue = control.value?.trim() || '';
    setRequestCallback(trimmedValue);
  }
}

/** Removes leading/trailing spaces from a form control's value. */
export function trimControlValue(formGroup: FormGroup, controlName: string): void {
  const control = formGroup.get(controlName);
  const value = control?.value;
  if (typeof value === 'string' && value !== value.trim()) {
    control?.setValue(value.trim());
  }
}

interface IChartYAxisIntervals {
  max: number;
  interval: number;
}

export function getChartYAxisIntervalObject(maxValue: number): IChartYAxisIntervals {
  for (let i = 0; i < ChartYAxisIntervals.list.length; i++) {
    if (ChartYAxisIntervals.list[i].max >= maxValue) {
      return ChartYAxisIntervals.list[i];
    }
  }

  return { max: maxValue > 0 ? maxValue : 0, interval: maxValue > 5 ? maxValue / 5 : 5 };
}

export function getCurrentTime() {
  const now = new Date();
  return now.toLocaleTimeString('en-US', {
    hour: 'numeric',
    minute: '2-digit',
    hour12: true,
  });
}

export function downloadFileFromUTF8Stream(fileStream: any, fileName: string) {
  if (fileStream?.length > 0) {
    const uint8array = Buffer.from(fileStream.toString(), 'base64');
    const bom = new Uint8Array([0xef, 0xbb, 0xbf]);
    const file = new Blob([bom, uint8array], { type: 'text/csv;charset=utf-8;' });
    const a = document.createElement('a');
    const url = window.URL.createObjectURL(file);
    a.href = url;
    a.download = fileName;
    document.body.appendChild(a);
    a.click();
    window.URL.revokeObjectURL(url);
  }
}

export function getElementWidth(elementRef?: any, selector?: string): string | null {
  if (elementRef?.nativeElement) {
    const width = elementRef.nativeElement.getBoundingClientRect().width;
    return `${Math.floor(width)}px`;
  }

  if (selector) {
    const el = document.querySelector(selector) as HTMLElement;
    if (el) {
      const width = el.getBoundingClientRect().width;
      return `${Math.floor(width)}px`;
    }
  }

  return null;
}

export class QueryParamHelper {
  static extractMetaAdParams(
    route: ActivatedRoute,
    router: Router,
  ): {
    strategy: boolean;
    adId: number;
    adSetid: number;
    adCampaignId: number;
    adGoalId: number | null;
    adStrategyId: number | null;
    adCreativeId: number | null;
    adTypeId: number | null;
    creativeTypeId: number | null;
    destinationTypeId: number | null;
    isfirstad: boolean | null;
    isAdStrategyCompleted: boolean;
    isAdGoalCompleted: boolean;
    isAdMessagingStrategyCompleted: boolean;
    isAdDestinationCompleted: boolean;
    isCreativesCompleted: boolean;
    isAudienceCompleted: boolean;
    isBudgetCompleted: boolean;
    isReviewCompleted: boolean;
    isOnboardingComplete: boolean;
    uploadedMediaContent?: any[] | null;
    uploadedMediaContentForPreview?: any[] | null;
    metaAdId: string | '';
    isLeadAd: boolean | false;
    issourceid: number | null;
    businessxoopahadcampaignstrategyid: number | null;
    xoopahstrategyid: number | null;
    isfromadcampaign: boolean | null;
    isretargetingad: boolean | null;
    iscampaignfirst: boolean | null;
    startTime: string | null;
    iscreatedfromugc: boolean | null;
  } | null {
    let strategy = false;
    let adId = 0;
    let adSetid = 0;
    let adCampaignId = 0;
    let adGoalId: number | null = null;
    let adStrategyId: number | null = null;
    let adCreativeId: number | null = null;
    let adTypeId: number | null = null;
    let creativeTypeId: number | null = null;
    let destinationTypeId: number | null = null;
    let isfirstad: boolean | null = null;
    let issourceid: number | null = null;
    let businessxoopahadcampaignstrategyid: number | null = null;
    let xoopahstrategyid: number | null = null;
    let isfromadcampaign: boolean | null = null;
    let isretargetingad: boolean | null = null;
    let iscampaignfirst: boolean | null = null;
    let startTime: string | null = null;
    let iscreatedfromugc: boolean | null = null;

    let isAdStrategyCompleted = false;
    let isAdGoalCompleted = false;
    let isAdMessagingStrategyCompleted = false;
    let isAdDestinationCompleted = false;
    let isCreativesCompleted = false;
    let isAudienceCompleted = false;
    let isBudgetCompleted = false;
    let isReviewCompleted = false;
    let isOnboardingComplete = false;
    let uploadedMediaContent: any[] | null = null;
    let uploadedMediaContentForPreview: any[] | null = null;
    let metaAdId: string | '' = '';
    let isLeadAd: boolean | false = false;

    const params = route.snapshot.queryParams;

    if (params['data']) {
      try {
        const decodedData = atob(params['data']);
        const payload = JSON.parse(decodedData);

        strategy = payload.strategy === true || payload.strategy === 'true';
        adId = Number(payload.adid);
        adSetid = Number(payload.adsetid);
        adCampaignId = Number(payload.adcampaignid);
        adGoalId = payload.adgoalid ? Number(payload.adgoalid) : null;
        adStrategyId = payload.adstrategyid ? Number(payload.adstrategyid) : null;
        adCreativeId = payload.adcreativeid ? Number(payload.adcreativeid) : null;
        adTypeId = payload.adtypeid ? Number(payload.adtypeid) : null;
        creativeTypeId = payload.creativetype ? Number(payload.creativetype) : (payload.creativeType ? Number(payload.creativeType) : null);
        destinationTypeId = payload.destinationtypeid ? Number(payload.destinationtypeid) : null;
        isfirstad = payload.isfirstad !== undefined ? !!payload.isfirstad : null;
        issourceid = payload.issourceid ? Number(payload.issourceid) : null;
        businessxoopahadcampaignstrategyid = payload.businessxoopahadcampaignstrategyid ? Number(payload.businessxoopahadcampaignstrategyid) : null;
        xoopahstrategyid = payload.xoopahstrategyid ? Number(payload.xoopahstrategyid) : null;
        isfromadcampaign = payload.isfromadcampaign !== undefined ? !!payload.isfromadcampaign : null;
        isretargetingad = payload.isretargetingad !== undefined ? !!payload.isretargetingad : null;
        iscampaignfirst = payload.iscampaignfirst !== undefined ? !!payload.iscampaignfirst : null;
        startTime = payload.starttime || payload.startTime || null;
        iscreatedfromugc = payload.iscreatedfromugc !== undefined ? !!payload.iscreatedfromugc : null;

        isAdStrategyCompleted = !!payload.isadstrategycompleted;
        isAdGoalCompleted = !!payload.isadgoalcompleted;
        isAdMessagingStrategyCompleted = !!payload.isadmessagingstrategycompleted;
        isAdDestinationCompleted = !!payload.isaddestinationcompleted;
        isCreativesCompleted = !!payload.iscreativescompleted;
        isAudienceCompleted = !!payload.isaudiencecompleted;
        isBudgetCompleted = !!payload.isbudgetcompleted;
        isReviewCompleted = !!payload.isreviewcompleted;
        isOnboardingComplete = !!payload.isonboardingcomplete;

        uploadedMediaContent = payload.uploadedMediaContent || null;
        uploadedMediaContentForPreview = payload.uploadedMediaContentForPreview || null;
        metaAdId = payload.metaAdId || '';
        isLeadAd = !!payload.isLeadAd;
      } catch (error) {
        console.error('Error decoding data:', error);
        router.navigate(['/meta-ads/meta-ads-dashboard/meta-ads-listing']);
        return null;
      }
    } else {
      strategy = params['strategy'] === 'true';
      adId = Number(params['adid']);
      adSetid = Number(params['adsetid']);
      adCampaignId = Number(params['adcampaignid']);
      adGoalId = params['adgoalid'] ? Number(params['adgoalid']) : null;
      adStrategyId = params['adstrategyid'] ? Number(params['adstrategyid']) : null;
      adCreativeId = params['adcreativeid'] ? Number(params['adcreativeid']) : null;
      adTypeId = params['adtypeid'] ? Number(params['adtypeid']) : null;
      creativeTypeId = params['creativetype'] ? Number(params['creativetype']) : (params['creativeType'] ? Number(params['creativeType']) : null);
      destinationTypeId = params['destinationtypeid'] ? Number(params['destinationtypeid']) : null;
      isfirstad = params['isfirstad'] !== undefined ? params['isfirstad'] === 'true' : null;
      issourceid = params['issourceid'] ? Number(params['issourceid']) : null;
      businessxoopahadcampaignstrategyid = params['businessxoopahadcampaignstrategyid'] ? Number(params['businessxoopahadcampaignstrategyid']) : null;
      xoopahstrategyid = params['xoopahstrategyid'] ? Number(params['xoopahstrategyid']) : null;
      isfromadcampaign = params['isfromadcampaign'] !== undefined ? params['isfromadcampaign'] === 'true' : null;
      isretargetingad = params['isretargetingad'] !== undefined ? params['isretargetingad'] === 'true' : null;
      iscampaignfirst = params['iscampaignfirst'] !== undefined ? params['iscampaignfirst'] === 'true' : null;
      startTime = params['starttime'] || params['startTime'] || null;
      iscreatedfromugc = params['iscreatedfromugc'] !== undefined ? params['iscreatedfromugc'] === 'true' : null;

      isAdStrategyCompleted = params['isadstrategycompleted'] === 'true';
      isAdGoalCompleted = params['isadgoalcompleted'] === 'true';
      isAdMessagingStrategyCompleted = params['isadmessagingstrategycompleted'] === 'true';
      isAdDestinationCompleted = params['isaddestinationcompleted'] === 'true';
      isCreativesCompleted = params['iscreativescompleted'] === 'true';
      isAudienceCompleted = params['isaudiencecompleted'] === 'true';
      isBudgetCompleted = params['isbudgetcompleted'] === 'true';
      isReviewCompleted = params['isreviewcompleted'] === 'true';
      isOnboardingComplete = params['isonboardingcomplete'] === 'true';
      metaAdId = params['metaAdId'] || '';
      isLeadAd = params['isLeadAd'] === 'true';
    }

    // Skip ID validation if coming from ad campaign creation
    if (!isfromadcampaign && (!adId || !adSetid || !adCampaignId)) {
      console.warn('Invalid IDs received:', { adId, adSetid, adCampaignId });
      router.navigate(['/meta-ads/meta-ads-dashboard/meta-ads-listing']);
      return null;
    }

    return {
      strategy,
      adId,
      adSetid,
      adCampaignId,
      adGoalId,
      adStrategyId,
      adCreativeId,
      adTypeId,
      creativeTypeId,
      destinationTypeId,
      isfirstad,
      issourceid,
      isAdStrategyCompleted,
      isAdGoalCompleted,
      isAdMessagingStrategyCompleted,
      isAdDestinationCompleted,
      isCreativesCompleted,
      isAudienceCompleted,
      isBudgetCompleted,
      isReviewCompleted,
      isOnboardingComplete,
      uploadedMediaContent,
      uploadedMediaContentForPreview,
      metaAdId,
      isLeadAd,
      businessxoopahadcampaignstrategyid,
      xoopahstrategyid,
      isfromadcampaign,
      isretargetingad,
      iscampaignfirst,
      startTime,
      iscreatedfromugc
    };
  }
}
